import { useState } from 'react';
import { Search, Loader2, CheckCircle2, MapPin, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { hubAPI } from '../lib/api';
import { toast } from 'sonner';

const CoverageChecker = ({ onFocusHub }) => {
  const [address, setAddress] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const check = async () => {
    const trimmed = address.trim();
    if (trimmed.length < 3) {
      toast.error('Enter an address or ZIP');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await hubAPI.checkCoverage(trimmed, result?.in_coverage === false ? email : undefined);
      setResult(res.data);
      if (res.data.in_coverage && onFocusHub) {
        onFocusHub(res.data.nearest_hub.id);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not check that address');
    } finally {
      setLoading(false);
    }
  };

  const submitEmail = async () => {
    if (!email.includes('@')) { toast.error('Please enter a valid email'); return; }
    setLoading(true);
    try {
      const res = await hubAPI.checkCoverage(address.trim(), email);
      setResult(res.data);
      if (res.data.lead_captured) {
        toast.success("Thanks! We'll let you know when we expand to your area.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto mb-8" data-testid="coverage-checker">
      <div className="bg-white border border-zinc-200 rounded-2xl shadow-sm p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-3">
          <Search className="w-4 h-4 text-orange-500" />
          <p className="text-sm font-semibold text-zinc-800">Check if we serve your address</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            value={address}
            onChange={e => setAddress(e.target.value)}
            placeholder="e.g., 62269 or 100 Main St, O'Fallon, IL"
            className="flex-1"
            onKeyDown={e => { if (e.key === 'Enter') check(); }}
            data-testid="coverage-address-input"
          />
          <Button
            onClick={check}
            disabled={loading || address.trim().length < 3}
            className="bg-orange-500 hover:bg-orange-600 text-white sm:w-32"
            data-testid="coverage-check-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Check<Search className="w-4 h-4 ml-1" /></>}
          </Button>
        </div>

        {result && (
          <div className={`mt-3 rounded-xl p-3 sm:p-4 ${result.in_coverage ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`} data-testid="coverage-result">
            {result.in_coverage ? (
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-green-900">
                    Yes! We serve your area.
                  </p>
                  <p className="text-sm text-green-800 mt-0.5">
                    Closest hub is <strong>{result.nearest_hub.name}, {result.nearest_hub.state}</strong> — about <strong>{result.distance_miles} miles</strong> away.
                  </p>
                  {onFocusHub && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-green-700 hover:text-green-900 hover:bg-green-100 mt-1 -ml-2"
                      onClick={() => onFocusHub(result.nearest_hub.id)}
                      data-testid="coverage-show-on-map-btn"
                    >
                      <MapPin className="w-3.5 h-3.5 mr-1" /> Show on map
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-amber-900">
                      Not quite yet — but we're expanding.
                    </p>
                    <p className="text-sm text-amber-800 mt-0.5">
                      Your closest hub is <strong>{result.nearest_hub.name}, {result.nearest_hub.state}</strong>, about <strong>{result.distance_miles} miles</strong> away (we currently serve up to {result.max_coverage_miles} miles from each hub).
                    </p>
                  </div>
                </div>
                {!result.lead_captured ? (
                  <div className="flex flex-col sm:flex-row gap-2 pt-1">
                    <Input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="flex-1"
                      onKeyDown={e => { if (e.key === 'Enter') submitEmail(); }}
                      data-testid="coverage-email-input"
                    />
                    <Button
                      onClick={submitEmail}
                      disabled={loading || !email.includes('@')}
                      variant="outline"
                      className="sm:w-40 border-amber-300 text-amber-900 hover:bg-amber-100"
                      data-testid="coverage-email-submit-btn"
                    >
                      <Mail className="w-4 h-4 mr-1" /> Notify me
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-amber-900 font-medium pl-8">
                    ✓ Got it — we'll email <strong>{email}</strong> when we expand to your area.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CoverageChecker;
